/**
 * PATH: packages/bills/src/components/BillEmailPanel.tsx
 * FILE_ID: BILLS-PKG-CMP-EMAIL-001
 * PURPOSE: Right-rail "Email" tab — read the bill's source email inline.
 *
 * WHY: Bookkeepers triaging an extracted bill often need to glance at
 *      the source email (subject, sender, body, attachments) to
 *      decide whether the AI got it right. Previously they had to
 *      open `/email/<id>` in a new tab; jake 2026-05-05: "I want to
 *      read the email real quick on the right drawer".
 *
 *      We use the injected `fetchEmailDetail` if the app wired one;
 *      otherwise we fall back to the summary fields already on the
 *      BillDetail (subject + preview), with a CTA to open the full
 *      email in the Email Hub. That keeps the package useful even
 *      before each app finishes the adapter wiring.
 *
 * FLOW:
 *   bill.email_id  →  fetchEmailDetail(emailId)  →  EmailHeader + body
 *      │                       │
 *      └── (no fetcher) ──→ summary view (subject/from/preview)
 */

import { useEffect, useMemo, useState } from 'react';
import { isExpiredSignedUrl } from '@quotech/ui';
import { useBillsClient } from '../client';
import type {
  BillDetail,
  BillEmailDetail,
  BillEmailRecipient,
  ConvertEmailToPoInput,
} from '../types';

interface Props {
  bill: BillDetail | null;
}

function formatRecipientList(list: BillEmailRecipient[] | undefined): string {
  if (!list || list.length === 0) return '';
  return list
    .map((r) => {
      const name = (r.name || '').trim();
      const email = (r.email || '').trim();
      if (name && email) return `${name} <${email}>`;
      return email || name;
    })
    .filter(Boolean)
    .join(', ');
}

export function BillEmailPanel({ bill }: Props) {
  const client = useBillsClient();
  const emailId = bill?.email_id || null;
  const fetcher = client.fetchEmailDetail;
  const sanitiser = client.sanitiseHtml;
  // When the host app injects its canonical Email Hub viewer we REUSE it
  // (one email reader, not three) instead of the bespoke fallback below.
  const EmailView = client.EmailView;

  const [detail, setDetail] = useState<BillEmailDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The injected EmailView does its own fetching (email-hub useEmail), so the
    // bespoke fetch is dead weight — and a duplicate request — when it's wired.
    if (EmailView || !emailId || typeof fetcher !== 'function') {
      setDetail(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetcher(emailId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load email');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [emailId, fetcher, EmailView]);

  const sanitisedHtml = useMemo(() => {
    const html = detail?.body_html;
    if (!html || typeof sanitiser !== 'function') return null;
    /* Rewrite ``<img src="cid:VALUE">`` refs to the signed inline-serve
     * URL when the matching attachment row carries ``inline_url``.
     * Refs that don't resolve are left in place (broken icon better
     * than silently dropped content — operator complaint 2026-05-09:
     * "image placements inline of the emails still stands"). The
     * injected sanitiser then runs DOMPurify so script/iframe/onclick
     * are stripped consistently with the rest of the app. */
    const cidMap = new Map<string, string>();
    for (const att of detail?.attachments ?? []) {
      const cid = (att.content_id || '').trim().replace(/^<|>$/g, '').toLowerCase();
      // Skip signed URLs that have already expired — emitting one just
      // guarantees a 410 on every re-render (2026-07-24 "constantly
      // getting called with the error"). The cid: ref stays in place.
      if (cid && att.inline_url && !isExpiredSignedUrl(att.inline_url)) {
        cidMap.set(cid, att.inline_url);
      }
    }
    const rewritten = html.replace(
      /<img\b([^>]*?)\bsrc\s*=\s*["']cid:([^"']*)["']([^>]*)>/gi,
      (match, before: string, cidRaw: string, after: string) => {
        const url = cidMap.get(cidRaw.trim().replace(/^<|>$/g, '').toLowerCase());
        return url ? `<img${before}src="${url}"${after}>` : match;
      },
    );
    return sanitiser(rewritten);
  }, [detail?.body_html, detail?.attachments, sanitiser]);

  if (!bill) {
    return <Empty>Select a bill to read its source email.</Empty>;
  }

  /* PREFERRED PATH — reuse the app's canonical Email Hub reader (the SAME
   * full reader the kanban card drawer's Email tab mounts — search-in-email,
   * tags, right-click context menu, smart actions, attachment preview). The
   * reader is a full-height pane that owns its own scrolling, so it gets a
   * bounded flex slot rather than a document-flow scroll container. The
   * bills-only "Convert to PO" callout sits above it so that action survives
   * the switch away from the bespoke reader. */
  if (EmailView) {
    return (
      <aside className="flex h-full min-h-0 w-full flex-col border-l border-border bg-card">
        {emailId && client.convertEmailToPo && (
          <div className="shrink-0 p-3 pb-0">
            <ConvertToPoCallout emailId={emailId} bill={bill} detail={null} />
          </div>
        )}
        {emailId ? (
          <div className="min-h-0 flex-1">
            <EmailView emailId={emailId} />
          </div>
        ) : (
          <p className="p-3 text-xs text-muted-foreground">
            No source email is linked to this bill.
          </p>
        )}
      </aside>
    );
  }

  /* No fetcher and no email id → degenerate fallback. Show what we
   * already know from the bill itself so the tab still has content. */
  const hasFetcher = typeof fetcher === 'function';

  return (
    <aside className="flex h-full min-h-0 w-full flex-col border-l border-border bg-card">
      <header className="shrink-0 border-b border-border bg-card px-3 py-2">
        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Source email
        </p>
        <h2 className="truncate text-sm font-semibold tracking-tight">
          {detail?.subject || bill.email_subject || '(no subject)'}
        </h2>
        <p className="truncate text-xs text-muted-foreground">
          From {detail?.from_name || detail?.from_address || bill.email_from || '—'}
          {bill.email_received_at && (
            <> · {new Date(bill.email_received_at).toLocaleString()}</>
          )}
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {loading && (
          <p className="text-xs text-muted-foreground">Loading email…</p>
        )}
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        {/* PR-2: when this email looks more like a quote / payment-
            request than a bill (the AI sometimes mis-classifies
            "please pay COD before we start" as a bill), the
            bookkeeper can spawn a PO from the email rather than
            treating it as an accounts-payable invoice. */}
        {emailId && client.convertEmailToPo && (
          <ConvertToPoCallout
            emailId={emailId}
            bill={bill}
            detail={detail}
          />
        )}

        {!hasFetcher && !loading && !error && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <p className="font-medium">Quick preview only</p>
            <p className="mt-0.5">
              Full email body isn't wired into this app yet — the
              snippet below is the AI extraction preview.
            </p>
          </div>
        )}

        {detail && (
          <RecipientBlock
            to={detail.to_addresses}
            cc={detail.cc_addresses}
            subject={detail.subject}
          />
        )}

        {detail?.attachments && detail.attachments.length > 0 && (
          <section className="mt-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Attachments ({detail.attachments.length})
            </p>
            <ul className="space-y-1 text-xs">
              {detail.attachments.map((a, i) => (
                <li key={a.id ?? i} className="truncate text-foreground">
                  · {a.filename || `attachment-${i + 1}`}
                  {a.size != null && (
                    <span className="ml-1 text-muted-foreground">
                      ({Math.max(1, Math.round((a.size as number) / 1024))} KB)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-3 border-t border-border pt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Message
          </p>
          {sanitisedHtml ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none break-words text-foreground"
              dangerouslySetInnerHTML={{ __html: sanitisedHtml }}
            />
          ) : (
            <pre className="whitespace-pre-wrap break-words bg-transparent p-0 font-sans text-sm text-foreground">
              {detail?.body_text ||
                detail?.body_preview ||
                bill.email_body_preview ||
                '(no body)'}
            </pre>
          )}
        </section>
      </div>
    </aside>
  );
}

function RecipientBlock({
  to,
  cc,
  subject,
}: {
  to?: BillEmailRecipient[];
  cc?: BillEmailRecipient[];
  subject?: string;
}) {
  const toLine = formatRecipientList(to);
  const ccLine = formatRecipientList(cc);
  if (!toLine && !ccLine && !subject) return null;
  return (
    <section className="space-y-1 text-xs">
      {toLine && (
        <div className="flex gap-2">
          <span className="w-10 shrink-0 font-medium uppercase text-muted-foreground">To</span>
          <span className="break-words text-foreground">{toLine}</span>
        </div>
      )}
      {ccLine && (
        <div className="flex gap-2">
          <span className="w-10 shrink-0 font-medium uppercase text-muted-foreground">Cc</span>
          <span className="break-words text-foreground">{ccLine}</span>
        </div>
      )}
      {subject && (
        <div className="flex gap-2">
          <span className="w-10 shrink-0 font-medium uppercase text-muted-foreground">Subj</span>
          <span className="break-words font-medium text-foreground">{subject}</span>
        </div>
      )}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <aside className="flex h-full w-full items-center justify-center border-l border-border bg-card p-6 text-center text-sm text-muted-foreground">
      <div>{children}</div>
    </aside>
  );
}

/**
 * Convert-to-PO inline action shown above the email body.
 *
 * The bookkeeper sees this when looking at the source email of a
 * bill that they think shouldn't have been classified as a bill at
 * all — e.g. a COD payment request that the AI mis-routed. Clicking
 * spawns a ``XeroPurchaseOrder(source='email_extraction')`` row
 * pre-filled from the bill's already-extracted vendor / total /
 * line_items, and links it back to this email + the largest
 * attachment for audit.
 *
 * Pre-fill strategy
 * -----------------
 * We trust the bill's own extraction over re-running the AI: the
 * vendor + total are usually correct (the misclassification was at
 * the routing layer, not the extraction layer). The bookkeeper
 * tweaks the supplier name + total in the inline form before
 * submit if needed — same shape as the existing CreatePoForm.
 */
function ConvertToPoCallout({
  emailId,
  bill,
  detail,
}: {
  emailId: string;
  bill: BillDetail;
  detail: BillEmailDetail | null;
}) {
  const client = useBillsClient();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successPoId, setSuccessPoId] = useState<number | null>(null);

  // Inline form state — pre-filled from the bill extraction so the
  // bookkeeper's most common click is "approve as-is".
  const [supplier, setSupplier] = useState(bill.vendor_name || '');
  const [total, setTotal] = useState(bill.total || '');
  const [reference, setReference] = useState(bill.reference || '');
  const [poNumber, setPoNumber] = useState('');

  const largestAttachmentId = useMemo(() => {
    const list = detail?.attachments ?? [];
    if (list.length > 0) {
      const top = [...list].sort((a, b) => (b.size ?? 0) - (a.size ?? 0))[0];
      if (top?.id != null) return String(top.id);
    }
    // Reuse path passes no email detail (the injected viewer fetches its own),
    // so fall back to the bill's own source attachment — which is exactly the
    // document we'd want linked to the spawned PO anyway.
    return bill.attachment_id != null ? String(bill.attachment_id) : null;
  }, [detail?.attachments, bill.attachment_id]);

  if (!client.convertEmailToPo) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: ConvertEmailToPoInput = {
        purchase_order_number: poNumber.trim() || null,
        supplier_name: supplier.trim() || null,
        reference: reference.trim() || null,
        total: total || '0.00',
        currency_code: bill.currency || 'AUD',
        date: bill.invoice_date || null,
        attachment_id: largestAttachmentId,
        // Carry the bill's extracted line items across — bookkeeper
        // can edit on the PO panel after creation.
        line_items: (bill.line_items || []).map((li) => ({
          description: typeof li.description === 'string' ? li.description : '',
          quantity: li.quantity != null ? String(li.quantity) : null,
          unit_amount: li.unit_amount != null ? String(li.unit_amount) : null,
          line_amount: li.line_amount != null ? String(li.line_amount) : null,
        })),
      };
      const po = await client.convertEmailToPo!(emailId, input);
      setSuccessPoId(po.id);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert');
    } finally {
      setSubmitting(false);
    }
  };

  if (successPoId !== null) {
    return (
      <div className="mb-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
        Converted to PO #{successPoId}. Open the right-rail{' '}
        <span className="font-medium">PO</span> tab to record a payment
        against it.
      </div>
    );
  }

  return (
    <div className="mb-3 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium">Looks like a quote or payment request?</p>
          <p className="mt-0.5 text-[11px] text-sky-800 dark:text-sky-200/90">
            Convert this email to a Purchase Order so you can track
            COD / deposit payments against it before the bill arrives.
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md border border-sky-400 bg-white px-2 py-1 text-[11px] font-medium text-sky-900 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-950 dark:text-sky-200 dark:hover:bg-sky-900/40"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Cancel' : 'Convert to PO'}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-[11px] text-rose-700 dark:text-rose-300">
          {error}
        </p>
      )}
      {open && (
        <form onSubmit={handleSubmit} className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-0.5">
              <span className="block text-[10px] uppercase tracking-wide text-sky-800 dark:text-sky-200/90">
                Supplier
              </span>
              <input
                type="text"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                className="w-full rounded border border-sky-300 bg-white px-2 py-1 text-xs text-foreground dark:border-sky-700 dark:bg-sky-950"
              />
            </label>
            <label className="space-y-0.5">
              <span className="block text-[10px] uppercase tracking-wide text-sky-800 dark:text-sky-200/90">
                Total
              </span>
              <input
                type="number"
                step="0.01"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                className="w-full rounded border border-sky-300 bg-white px-2 py-1 text-xs text-foreground dark:border-sky-700 dark:bg-sky-950"
              />
            </label>
            <label className="space-y-0.5">
              <span className="block text-[10px] uppercase tracking-wide text-sky-800 dark:text-sky-200/90">
                PO number
              </span>
              <input
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                className="w-full rounded border border-sky-300 bg-white px-2 py-1 text-xs text-foreground dark:border-sky-700 dark:bg-sky-950"
                placeholder="optional"
              />
            </label>
            <label className="space-y-0.5">
              <span className="block text-[10px] uppercase tracking-wide text-sky-800 dark:text-sky-200/90">
                Reference
              </span>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full rounded border border-sky-300 bg-white px-2 py-1 text-xs text-foreground dark:border-sky-700 dark:bg-sky-950"
                placeholder="optional"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-sky-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {submitting ? 'Converting…' : 'Create PO from this email'}
          </button>
        </form>
      )}
    </div>
  );
}
